// src/components/ReviewsCarousel.jsx
import React from 'react';
import styled from 'styled-components';

const Wrap = styled.section` margin: 26px 0; `;
const Head = styled.div` margin-bottom:12px; h2{margin:0;font-size:26px;} `;


const Center = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
`;

/* ВАЖНО: задаём фиксированную max-width и центрируем секцию */
const Rail = styled.div`
  position: relative;
  overflow: hidden;
  outline: none;
  width: 100%;
  max-width: ${p =>
    typeof p.$maxWidth === 'number'
      ? `${p.$maxWidth}px`
      : (p.$maxWidth || '100%')};
  margin: 0 auto;
  padding: 0;
  box-sizing: border-box;
`;

const Track = styled.div`
  display: flex;
  will-change: transform;
  transition: transform .45s ease;
  transform: ${p => `translate3d(-${p.$idx * (100 / p.$perView)}%,0,0)`}; /* без субпикс. дрожи */
`;



const Item = styled.div`
  flex: 0 0 ${p => 100 / p.$perView}%;
  box-sizing: border-box;
  padding: 0;                 /* без боковых отступов */
  display: flex;
  justify-content: center;    /* карточка по центру */
`;

const Card = styled.article`
  border: 1px solid var(--bdr);
  border-radius: 18px;
  background: var(--card);
  box-shadow: var(--shadow);
  padding: 18px;

  /* РАНЬШЕ: max-width:420px — из-за него карточки были узкими.
     Даем занять всю ширину своей колонки и центрируем на 1-в-ряд. */
  width: 100%;
  max-width: ${p => (p.$isSingle ? 'clamp(340px, 92vw, 560px)' : '100%')};
  margin: 0 auto;

  display: flex;
  flex-direction: column;
  gap: 14px;

  .name{ 
    font-weight:800; 
    font-size:16px; 
    color: #000;
    background: var(--gold);
    opacity: 0.92;
    padding: 8px 12px;
    border-radius: 12px;
    backdrop-filter: blur(8px);
    display: inline-block;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.15);
    border: 1px solid rgba(255, 255, 255, 0.3);
    margin-right: 8px;
  }
  .role{ 
    color: #000;
    background: var(--gold);
    opacity: 0.88;
    padding: 6px 10px;
    border-radius: 10px;
    backdrop-filter: blur(6px);
    display: inline-block;
    box-shadow: 0 1px 8px rgba(0, 0, 0, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.25);
    font-size:13px;
  }
  .text{ margin-top:8px; font-size:15px; line-height:1.6; }
`;

const Controls = styled.div`
  display: grid;
  gap: 8px;
  margin: 12px auto 0;
  justify-items: center;
  max-width: clamp(340px, 92vw, 560px);
  margin: 12px auto 0;
  padding: 0;
  box-sizing: border-box;
`;

const Arrows = styled.div`
  display:flex; gap:8px; justify-content:center;
  button{
    width:36px;height:36px;border-radius:12px;border:1px solid var(--bdr);background:var(--card);
    display:grid; place-items:center; cursor:pointer;
    &:disabled{ opacity:.5; cursor:default; }
  }
`;
const Dots = styled.div`
  display:flex; gap:6px; justify-content:center;
  button{
    width:24px;height:8px;border-radius:4px; border:none; background:var(--bdr); padding:0; cursor:pointer;
    transition: background .15s ease;
  }
  button[aria-current="true"]{ background:var(--gold); }
`;

/* адаптив */
function makePerViewGetter({
  bpTiny = 640,
  bpTablet = 980,
  perViewTiny = 1,
  perViewMobile = 1,
  perViewTablet = 2,
  perViewDesktop = 3,
}) {
  return (w) => {
    if (w <= bpTiny)   return perViewTiny;
    if (w <= bpTablet) return perViewTablet;
    return perViewDesktop;
  };
}

export default function ReviewsCarousel({
  title = 'Отзывы',
  items = [],
  perViewDesktop = 3,
  perViewTablet  = 1,
  perViewMobile  = 1,
  perViewTiny    = 1,
  bpTiny   = 640,
  bpTablet = 1000,
  autoplay = true,
  autoplayIntervalMs = 7000,
  pauseOnHover = true,
  /* НОВОЕ: ширина всего блока отзывов (по центру) */
  maxWidth = 1100,
}) {
  const getPerView = React.useMemo(
    () => makePerViewGetter({ bpTiny, bpTablet, perViewTiny, perViewMobile, perViewTablet, perViewDesktop }),
    [bpTiny, bpTablet, perViewTiny, perViewMobile, perViewTablet, perViewDesktop]
  );

  const [perView, setPerView] = React.useState(1);
  const [idx, setIdx] = React.useState(0);
  const [hovered, setHovered] = React.useState(false);

  React.useEffect(() => {
    const measure = () => setPerView(getPerView(window.innerWidth));
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [getPerView]);

  const maxIdx = Math.max(0, (items.length || 0) - perView);
  React.useEffect(() => { setIdx(i => Math.min(i, maxIdx)); }, [maxIdx]);
  React.useEffect(() => { setIdx(0); }, [perView]);

  const next = React.useCallback(() => setIdx(i => Math.min(maxIdx, i + 1)), [maxIdx]);
  const prev = React.useCallback(() => setIdx(i => Math.max(0, i - 1)), []);

  React.useEffect(() => {
    if (!autoplay || items.length <= perView) return;
    if (pauseOnHover && hovered) return;
    const t = setInterval(() => setIdx(i => (i >= maxIdx ? 0 : i + 1)), autoplayIntervalMs);
    return () => clearInterval(t);
  }, [autoplay, autoplayIntervalMs, items.length, perView, maxIdx, hovered, pauseOnHover]);

  if (!items.length) return null;

  const onKeyDown = (e) => {
    if (e.key === 'ArrowRight') next();
    if (e.key === 'ArrowLeft')  prev();
  };

  return (
    <Wrap>
      <Head>{title ? <h2>{title}</h2> : null}</Head>

      <Center>
      <Rail
        role="group"
        aria-roledescription="carousel"
        aria-label={title || 'Отзывы'}
        tabIndex={0}
        onKeyDown={onKeyDown}
        onMouseEnter={pauseOnHover ? () => setHovered(true) : undefined}
        onMouseLeave={pauseOnHover ? () => setHovered(false) : undefined}
        $maxWidth={maxWidth}       /* ← передаём ширину контейнера */
      >
        <Track $idx={idx} $perView={perView}>
          {items.map((r, i) => (
            <Item
              key={i}
              $perView={perView}
              aria-roledescription="slide"
              aria-label={`${i + 1} из ${items.length}`}
            >
              <Card $isSingle={perView === 1}>
                <div>
                  <div className="name">{r.name}</div>
                  {r.role ? <div className="role">{r.role}</div> : null}
                  {r.text ? <div className="text">“{r.text}”</div> : null}
                </div>
              </Card>
            </Item>
          ))}
        </Track>

        {items.length > perView && (
          <Controls>
            <Arrows>
              <button onClick={prev} aria-label="Предыдущие отзывы" disabled={idx === 0}>‹</button>
              <button onClick={next} aria-label="Следующие отзывы" disabled={idx === maxIdx}>›</button>
            </Arrows>
            <Dots role="tablist" aria-label="Пагинация отзывов">
              {Array.from({ length: maxIdx + 1 }).map((_, i) => (
                <button
                  key={i}
                  role="tab"
                  aria-label={`Страница ${i + 1}`}
                  aria-current={i === idx ? 'true' : 'false'}
                  onClick={() => setIdx(i)}
                />
              ))}
            </Dots>
          </Controls>
        )}
      </Rail>
      </Center>
    </Wrap>
  );
}
